import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { StatusBreakdownItem } from '../../features/analytics/api';
import { ordersPathForStatus } from '../../features/org-orders/ordersFilters';
import { STATUS_COLORS, STATUS_LABELS } from './statusMeta';

// The all-time status breakdown. Each segment and legend row links to the orders
// table pre-filtered to that status, so a click drills straight into the loads.
export function DonutChart({ data }: { data: StatusBreakdownItem[] }) {
  const navigate = useNavigate();
  const filtered = data.filter((d) => d.count > 0);
  const total = filtered.reduce((s, d) => s + d.count, 0);
  const [hovered, setHovered] = useState<string | null>(null);

  if (total === 0) {
    return (
      <div className="db-donut-wrap db-donut-wrap--empty">
        <span className="muted">No order data yet</span>
      </div>
    );
  }

  const R = 60;
  const C = 2 * Math.PI * R;
  let offset = 0;

  return (
    <div className="db-donut-wrap">
      <svg viewBox="0 0 160 160" className="db-donut-svg">
        {filtered.map((d) => {
          const pct = d.count / total;
          const dash = pct * C;
          const gap = C - dash;
          const rotation = (offset / total) * 360 - 90;
          const seg = (
            <circle
              key={d.status}
              cx="80"
              cy="80"
              r={R}
              fill="none"
              stroke={STATUS_COLORS[d.status] ?? '#94a3b8'}
              strokeWidth={hovered === d.status ? 20 : 16}
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={0}
              style={{
                transform: `rotate(${rotation}deg)`,
                transformOrigin: '80px 80px',
                transition: 'stroke-width 0.2s',
                cursor: 'pointer',
              }}
              onMouseEnter={() => setHovered(d.status)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => navigate(ordersPathForStatus(d.status))}
            >
              <title>{`View ${STATUS_LABELS[d.status] ?? d.status} orders`}</title>
            </circle>
          );
          offset += d.count;
          return seg;
        })}
        <text x="80" y="75" textAnchor="middle" className="db-donut-total">
          {total.toLocaleString()}
        </text>
        <text x="80" y="92" textAnchor="middle" className="db-donut-label">
          Total Loads
        </text>
      </svg>
      <div className="db-donut-legend">
        {filtered.map((d) => (
          <Link
            key={d.status}
            to={ordersPathForStatus(d.status)}
            className={`db-legend-item ${hovered === d.status ? 'db-legend-item--hovered' : ''}`}
            title={`View ${STATUS_LABELS[d.status] ?? d.status} orders`}
            onMouseEnter={() => setHovered(d.status)}
            onMouseLeave={() => setHovered(null)}
          >
            <span
              className="db-legend-dot"
              style={{ background: STATUS_COLORS[d.status] ?? '#94a3b8' }}
            />
            <span className="db-legend-status">{STATUS_LABELS[d.status] ?? d.status}</span>
            <span className="db-legend-count">{d.count.toLocaleString()}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
